const diagnosticMarker = Symbol("errorDiagnostics");
const responseDiagnostics = new WeakMap<object, { request_id?: string; status: number }>();

function findResponseDiagnostics(value: object) {
  const seen = new Set<object>();
  let current: unknown = value;
  while (current && typeof current === "object" && !seen.has(current)) {
    seen.add(current);
    const diagnostics = responseDiagnostics.get(current);
    if (diagnostics) return diagnostics;
    current = current instanceof Error ? current.cause : undefined;
  }
}

// Associate response/error objects without changing API payloads or serialising
// diagnostic fields into subsequent requests. Entries expire with their objects.
export function captureResponseDiagnostics(response: Response, ...values: unknown[]) {
  const header = response.headers.get("X-Request-ID");
  const diagnostics = {
    status: response.status,
    ...(header && /^[0-9a-f]{32}$/.test(header) ? { request_id: header } : {}),
  };
  for (const value of values) {
    if (value && typeof value === "object") responseDiagnostics.set(value, diagnostics);
  }
  return diagnostics;
}
const sensitiveKey =
  /^(?:password|pin|temp_pin|auth[_-]?token|.*_token|token|authorization|cookie|set-cookie|api[_-]?key|secret|patient|patient_details_form|lab_response|results_pdf)$/i;

export function redactText(value: string): string {
  return value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]")
    .replace(/\b(?:a|ap)\.[0-9a-f]{20,}\.[A-Za-z0-9_+/=-]+/gi, "[AUTH_TOKEN]")
    .replace(
      /((?:password|pin|token|secret|api[_-]?key|authorization|cookie)["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;&}]+)/gi,
      "$1[REDACTED]",
    );
}

function redactContext(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === "string") return redactText(value);
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (value instanceof Error) {
    // HTTP/provider errors can carry patient input in their messages and bodies.
    if (findResponseDiagnostics(value) || value instanceof SyntaxError || "response" in value || "status" in value)
      return getErrorDiagnostics(value);
    return { error_type: value.name, message: redactText(value.message), stack: redactText(value.stack ?? "") };
  }
  if (Array.isArray(value)) return value.map((item) => redactContext(item, seen));
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      sensitiveKey.test(key) ? "[REDACTED]" : redactContext(item, seen),
    ]),
  );
}

export function getErrorDiagnostics(error: unknown): Record<string | symbol, unknown> {
  const diagnostics: Record<string | symbol, unknown> = {
    [diagnosticMarker]: true,
    error_type: error instanceof Error ? error.constructor.name : "ApiError",
  };
  if (!error || typeof error !== "object") return diagnostics;
  const value = error as Record<string, unknown>;
  const code = value.code ?? value.error;
  if (typeof code === "string" && /^[A-Za-z][A-Za-z0-9_]{0,79}$/.test(code)) diagnostics.error_code = code;
  if (typeof code === "number") diagnostics.error_code = code;
  for (const key of ["status", "statusCode", "errno"]) {
    if (typeof value[key] === "number") diagnostics[key] = value[key];
  }
  Object.assign(diagnostics, findResponseDiagnostics(error));
  if (error instanceof Error) {
    if (/^[a-z]+(?:_[a-z0-9]+)+$/.test(error.message)) diagnostics.error_code = error.message;
    // Known runtime/network diagnostics are useful; arbitrary Error messages
    // may be whole provider responses or patient input.
    if (
      /^(Failed to fetch|fetch failed|NetworkError|Request failed)$/.test(error.message) ||
      (error instanceof TypeError &&
        /^(Cannot read properties of |Cannot destructure property |[A-Za-z_$][A-Za-z0-9_.$]* is not a function$)/.test(
          error.message,
        ))
    ) {
      diagnostics.message = error.message.replace(/"[^"\n]*"|'[^'\n]*'/g, "[REDACTED]");
    }
    const header = error.toString();
    const stack = error.stack?.startsWith(header) ? error.stack.slice(header.length) : error.stack;
    const frames = stack?.split("\n").filter((line) => /^\s+at /.test(line));
    if (frames?.length)
      diagnostics.stack = frames.map((line) => line.replace(/[?#][^\s)]*/g, "?[REDACTED]")).join("\n");
    if (error.cause instanceof Error && error.cause !== error) {
      const cause = error.cause as Error & { code?: unknown };
      if (typeof cause.code === "string" && /^[A-Z_0-9]+$/.test(cause.code)) diagnostics.cause_code = cause.code;
    }
  } else {
    diagnostics.stack = new Error().stack?.split("\n").slice(1).join("\n");
  }
  return Object.freeze(diagnostics);
}

export function getLogContext(args: unknown[]) {
  const context: Record<string, unknown> = {};
  const values: unknown[] = [];
  for (const arg of args) {
    if (arg instanceof Error) {
      context.error = redactContext(arg);
    } else if (arg && typeof arg === "object" && diagnosticMarker in arg) {
      context.error = arg;
    } else if (arg && typeof arg === "object" && !Array.isArray(arg)) {
      Object.assign(context, redactContext(arg));
    } else {
      values.push(redactContext(arg));
    }
  }
  if (values.length) context.values = values;
  return context;
}

export function reportError(event: string, error: unknown) {
  // JSON parsing and HTTP errors may embed entire response bodies. Ordinary
  // runtime errors retain their messages and stacks, including in the browser.
  console.error(event, error instanceof Error ? redactContext(error) : getErrorDiagnostics(error));
}
